import { useState, useRef, useMemo } from "react";

import { useDebounce } from "@calcom/lib/hooks/useDebounce";
import { trpc } from "@calcom/trpc/react";
import { ConfirmationDialogContent, Dialog, DropdownActions, showToast, Table, TextField } from "@calcom/ui";
import { Edit, Trash } from "@calcom/ui/components/icon";

const { Cell, ColumnTitle, Header, Row } = Table;

const FETCH_LIMIT = 100;

export function ProviderMeetingsTable() {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const utils = trpc.useContext();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [idToDelete, setIdToDelete] = useState<number | null>(null);

  const { data } = trpc.viewer.admin.languagesPaginated.useInfiniteQuery(
    {
      limit: FETCH_LIMIT,
      searchTerm: debouncedSearchTerm,
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const flatData = useMemo(() => data?.pages?.flatMap((page) => page.rows) ?? [], [data]);

  const mutation = trpc.viewer.spokenLanguages.delete.useMutation({
    onSuccess: async () => {
      showToast("Language has been deleted", "success");
      utils.viewer.admin.languagesPaginated.invalidate();
    },
    onError: (err: any) => {
      console.error(err.message);
      showToast("There has been an error deleting this language.", "error");
    },
    onSettled: () => {
      setSearchTerm("");
      setIdToDelete(null);
    },
  });

  return (
    <>
      <TextField
        placeholder="language name, abbr long abbr or original name ..."
        label="Search"
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <div
        className="border-subtle rounded-md border"
        ref={tableContainerRef}
        style={{
          height: "calc(100vh - 30vh)",
          overflow: "auto",
        }}>
        <Table>
          <Header>
            <ColumnTitle>#</ColumnTitle>
            <ColumnTitle>Name</ColumnTitle>
            <ColumnTitle>Original Name</ColumnTitle>
            <ColumnTitle>Abbr</ColumnTitle>
            <ColumnTitle>Long Abbr</ColumnTitle>
            <ColumnTitle widthClassNames="w-auto">
              <span className="sr-only">Edit</span>
            </ColumnTitle>
          </Header>

          <tbody className="divide-subtle divide-y rounded-md">
            {flatData.map((lang, index) => (
              <Row key={lang.id}>
                <Cell>{index + 1}</Cell>
                <Cell>{lang.name}</Cell>
                <Cell>{lang.originalName}</Cell>
                <Cell>{lang.abbreviation}</Cell>
                <Cell>{lang.longAbbreviation}</Cell>
                <Cell widthClassNames="w-auto">
                  <div className="flex w-full justify-end">
                    <DropdownActions
                      actions={[
                        {
                          id: "edit",
                          label: "Edit",
                          href: `/settings/admin/spoken-languages/${lang.id}/edit`,
                          icon: Edit,
                        },
                        {
                          id: "delete",
                          label: "Delete",
                          color: "destructive",
                          onClick: () => setIdToDelete(lang.id),
                          icon: Trash,
                        },
                      ]}
                    />
                  </div>
                </Cell>
              </Row>
            ))}
          </tbody>
        </Table>
        <DeleteDialog
          idToDelete={idToDelete}
          onClose={() => setIdToDelete(null)}
          onConfirm={() => {
            if (!idToDelete) return;
            mutation.mutate({ id: idToDelete });
          }}
        />
      </div>
    </>
  );
}

const DeleteDialog = ({
  idToDelete,
  onConfirm,
  onClose,
}: {
  idToDelete: number | null;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop
    <Dialog name="delete-lang" open={!!idToDelete} onOpenChange={(open) => (open ? () => {} : onClose())}>
      <ConfirmationDialogContent
        title="Delete Spoken Language"
        confirmBtnText="Delete"
        cancelBtnText="Cancel"
        variety="danger"
        onConfirm={onConfirm}>
        <p>Are you sure you want to delete this spoken language? All relations will be lost.</p>
      </ConfirmationDialogContent>
    </Dialog>
  );
};

export default ProviderMeetingsTable;
